import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ProfileEditForm = ({ user, onUpdate, loading }) => {
  const [formData, setFormData] = useState({
    name: user?.name,
    email: user?.email,
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [showPasswordSection, setShowPasswordSection] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear validation error when user starts typing
    if (validationErrors?.[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    // Name validation
    if (!formData?.name?.trim()) {
      errors.name = 'Name is required';
    }

    // Email validation
    if (!formData?.email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      errors.email = 'Please enter a valid email address';
    }

    // Password validation (only if changing password)
    if (showPasswordSection) {
      if (!formData?.currentPassword) {
        errors.currentPassword = 'Current password is required';
      }

      if (!formData?.newPassword) {
        errors.newPassword = 'New password is required';
      } else if (formData?.newPassword?.length < 6) {
        errors.newPassword = 'Password must be at least 6 characters';
      }

      if (!formData?.confirmPassword) {
        errors.confirmPassword = 'Please confirm your new password';
      } else if (formData?.newPassword !== formData?.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (validateForm()) {
      const updateData = {
        name: formData?.name,
        email: formData?.email
      };

      if (showPasswordSection && formData?.newPassword) {
        updateData.currentPassword = formData?.currentPassword;
        updateData.newPassword = formData?.newPassword;
      }

      onUpdate(updateData);
    }
  };

  const togglePasswordSection = () => {
    setShowPasswordSection(!showPasswordSection);
    if (!showPasswordSection) {
      // Clear password fields when hiding section
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
      // Clear password-related errors
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors?.currentPassword;
        delete newErrors?.newPassword;
        delete newErrors?.confirmPassword;
        return newErrors;
      });
    }
  };

  return (
    <div className="bg-card rounded-xl shadow-elevated border border-border p-6">
      <div className="flex items-center space-x-3 mb-6">
        <Icon name="Settings" size={24} color="var(--color-primary)" />
        <h2 className="text-xl font-heading font-semibold text-foreground">
          Profile Settings
        </h2>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground">Basic Information</h3>
          
          <Input
            label="Full Name"
            type="text"
            name="name"
            placeholder="Enter your full name"
            value={formData?.name}
            onChange={handleInputChange}
            error={validationErrors?.name}
            required
            disabled={loading}
          />

          <Input
            label="Email Address"
            type="email"
            name="email"
            placeholder="Enter your email"
            value={formData?.email}
            onChange={handleInputChange}
            error={validationErrors?.email}
            required
            disabled={loading}
            description="This will be your login email"
          />
        </div>

        {/* Password Section Toggle */}
        <div className="border-t border-border pt-6">
          <Button
            type="button"
            variant="ghost"
            onClick={togglePasswordSection}
            iconName={showPasswordSection ? "ChevronUp" : "ChevronDown"}
            iconPosition="right"
            className="mb-4"
          >
            {showPasswordSection ? 'Hide' : 'Change'} Password
          </Button>

          {showPasswordSection && (
            <div className="space-y-4 pl-4 border-l-2 border-primary/20">
              <h3 className="text-lg font-medium text-foreground">Change Password</h3>
              
              <Input
                label="Current Password"
                type="password"
                name="currentPassword"
                placeholder="Enter your current password"
                value={formData?.currentPassword}
                onChange={handleInputChange}
                error={validationErrors?.currentPassword}
                required
                disabled={loading}
              />

              <Input
                label="New Password"
                type="password"
                name="newPassword"
                placeholder="Enter your new password"
                value={formData?.newPassword}
                onChange={handleInputChange}
                error={validationErrors?.newPassword}
                required
                disabled={loading}
                description="Must be at least 6 characters"
              />

              <Input
                label="Confirm New Password"
                type="password"
                name="confirmPassword"
                placeholder="Confirm your new password"
                value={formData?.confirmPassword}
                onChange={handleInputChange}
                error={validationErrors?.confirmPassword}
                required
                disabled={loading}
              />
            </div>
          )}
        </div>

        {/* Submit Button */}
        <div className="flex justify-end pt-4">
          <Button
            type="submit"
            variant="default"
            loading={loading}
            disabled={loading}
            iconName="Save"
            iconPosition="left"
            className="bg-gradient-primary hover:opacity-90"
          >
            Save Changes
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ProfileEditForm;